import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow, format } from "date-fns";
import { cn } from "../../lib/utils";
import { crmApi } from "./api";
import { AssignLeadModal } from "./assign-lead-modal";
import { Modal } from "../../components/ui/modal";
import { ConfirmModal } from "../../components/ui/confirm-modal";
import { DateTimePicker } from "../../components/ui/date-picker";
import { NoteEditModal } from "./note-edit-modal";
import { ComposerToolbar } from "./composer-toolbar";
import { EmailComposer } from "./email-composer";
import { RichText } from "./rich-text";
import {
  Bell, Calendar, CalendarPlus, Check, CheckCircle2, ChevronDown, ChevronLeft,
  FileText, Mail, MessageCircle, MessageSquare, Paperclip, Phone, PhoneOutgoing,
  Play, Plus, Save, ShieldAlert, Sparkles, StickyNote, Trash2, UserCog, UserPlus, Users,
} from "lucide-react";
import { Select as _Select, SelectContent as _SelectContent, SelectItem as _SelectItem, SelectTrigger as _SelectTrigger, SelectValue as _SelectValue } from "../../components/ui/select";
import { CustomFieldInput } from "./custom-field-input";
import { LeadCallButton } from "./lead-call-button";
import { contactIdOf, leadStatus, avatarColorOf, initialsOf, appointmentPillCls, splitNoteTitle, SLA_REASON_LABELS } from "./types";
import type { CrmAppointment, Lead, LeadCampaign } from "./types";
import { ContactCallHistory } from "./contact-call-history";
import WhatsAppChatPanel from "../whatsapp-chat/chat-panel";

const Select = _Select as any;
const SelectContent = _SelectContent as any;
const SelectItem = _SelectItem as any;
const SelectTrigger = _SelectTrigger as any;
const SelectValue = _SelectValue as any;

interface LeadDetailPanelProps {
  lead: Lead;
  campaigns: LeadCampaign[];
  statuses: string[];
  onAddStatus: (status: string) => void;
  onSave: (id: string, data: Partial<Omit<Lead, "id" | "createdAt">>) => void;
  onDelete: (id: string) => void;
  /** Back to the list — rendered as a breadcrumb back button on the full record page. */
  onBack?: () => void;
  /** Display name for the list that opened this record. */
  listLabel?: string;
  /** SLA breach reasons for this lead, if any (from the overdue list). */
  overdueReasons?: string[];
}

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

/** "Today, 9:12 am" — matches the reference mockup's Last contact style. */
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const day = d.toDateString() === now.toDateString()
    ? "Today"
    : d.toDateString() === yesterday.toDateString()
      ? "Yesterday"
      : d.toLocaleDateString();
  let h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, "0");
  const ap = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  return `${day}, ${h}:${m} ${ap}`;
}

const ago = (iso?: string) => {
  if (!iso) return "—";
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return "—"; }
};

const fieldCls =
  "w-full rounded-lg border border-line bg-transparent px-3 py-1.5 text-xs text-ink outline-none focus:border-brand";
const labelCls = "mb-1 flex items-center gap-1.5 text-xs font-semibold text-ink-3";
const btnPrimaryCls =
  "inline-flex items-center gap-1 rounded-lg bg-brand text-white hover:bg-brand-hover shadow-xs px-2.5 py-1 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40";

type TabId = "activity" | "emails" | "comments" | "data" | "events" | "calls" | "tasks" | "notes" | "attachments";

const TABS: { id: TabId; label: string; icon: any }[] = [
  { id: "activity", label: "Activity", icon: Sparkles },
  { id: "emails", label: "Emails", icon: Mail },
  { id: "comments", label: "Comments", icon: MessageCircle },
  { id: "data", label: "Data", icon: FileText },
  { id: "events", label: "Events", icon: Calendar },
  { id: "calls", label: "Calls", icon: Phone },
  { id: "tasks", label: "Tasks", icon: CheckCircle2 },
  { id: "notes", label: "Notes", icon: StickyNote },
  { id: "attachments", label: "Attachments", icon: Paperclip },
];

const OUTCOME_PILL: Record<string, string> = {
  Interested: "bg-green-100 text-green-700",
  Connected: "bg-green-100 text-green-700",
  Callback: "bg-green-100 text-green-700",
  "Call Back": "bg-amber-100 text-amber-700",
  Busy: "bg-amber-100 text-amber-700",
  "No Answer": "bg-amber-100 text-amber-700",
  "Language issue": "bg-amber-100 text-amber-700",
  "Not Interested": "bg-red-100 text-red-700",
  "Wrong number": "bg-red-100 text-red-700",
};

export function LeadDetailPanel({ lead, campaigns, statuses, onAddStatus, onSave, onDelete, onBack, listLabel = "Leads", overdueReasons }: LeadDetailPanelProps) {
  const cache = useQueryClient();
  const team = useQuery({ queryKey: ["crm-team"], queryFn: crmApi.team });
  // Same keys the history/follow-ups views use, so these read from cache.
  const history = useQuery({ queryKey: ["crm-history", lead.id], queryFn: () => crmApi.history(lead.id) });
  const followUps = useQuery({ queryKey: ["crm-follow-ups"], queryFn: crmApi.followUps });
  const appointmentsQuery = useQuery({ queryKey: ["crm-appointments"], queryFn: crmApi.appointments });
  // The conversation is polled while it is on screen, so it stays closed until
  // an agent asks for it rather than polling for every lead they click through.
  const [chatOpen, setChatOpen] = useState(false);
  const [tab, setTab] = useState<TabId>("activity");
  const [addingStatus, setAddingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusError, setStatusError] = useState("");
  const changeStatus = (status: string) => onSave(lead.id, { status, fresh: status === "Fresh lead" });
  const addStatus = () => {
    const name = newStatus.trim();
    if (!name) { setStatusError("Enter a status name."); return; }
    if (statuses.some(status => status.toLowerCase() === name.toLowerCase())) {
      setStatusError("This status already exists. Choose it from the list."); return;
    }
    onAddStatus(name); changeStatus(name); setAddingStatus(false); setNewStatus(""); setStatusError("");
  };

  const [campaignId, setCampaignId] = useState(lead.campaignId);
  const campaign = campaigns.find((c) => c.id === campaignId) ?? null;
  const initialValues = () => lead.values || { company: lead.company || "", email_address: lead.email || "", source: lead.source || "" };
  const [values, setValues] = useState<Record<string, string>>(initialValues);
  // Top-level contact fields are editable here in the Data tab — the Details
  // rail on the right only displays them.
  const [editedName, setEditedName] = useState(lead.name || "");
  const [editedPhone, setEditedPhone] = useState(lead.phone || "");
  const [editedEmail, setEditedEmail] = useState(lead.email || "");
  const [assignOpen, setAssignOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [campaignOpen, setCampaignOpen] = useState(true);
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const [composerText, setComposerText] = useState("");
  const [composerBusy, setComposerBusy] = useState(false);
  const [composerError, setComposerError] = useState("");
  const [noteText, setNoteText] = useState("");
  const [noteTitle, setNoteTitle] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteError, setNoteError] = useState("");
  const [showNoteComposer, setShowNoteComposer] = useState(false);
  const [openNoteMenu, setOpenNoteMenu] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState<{ id: string; leadId: string; notes: string } | null>(null);
  const [deletingNote, setDeletingNote] = useState<{ id: string } | null>(null);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);
  const commentBoxRef = useRef<HTMLTextAreaElement>(null);
  const [recordingUrls, setRecordingUrls] = useState<Record<string, string>>({});
  const [loadingRecordings, setLoadingRecordings] = useState<Record<string, boolean>>({});
  const [openTranscripts, setOpenTranscripts] = useState<Record<string, boolean>>({});
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [followUpDue, setFollowUpDue] = useState("");
  const [followUpNote, setFollowUpNote] = useState("");
  const [followUpBusy, setFollowUpBusy] = useState(false);
  const [followUpError, setFollowUpError] = useState("");
  const [bookOpen, setBookOpen] = useState(false);
  const [bookDue, setBookDue] = useState("");
  const [bookNote, setBookNote] = useState("");
  const [bookBusy, setBookBusy] = useState(false);
  const [bookError, setBookError] = useState("");
  const [taskBusy, setTaskBusy] = useState(false);
  const [taskError, setTaskError] = useState("");
  const formRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);

  // Close the bottom composers on outside click / Escape — without a
  // fullscreen backdrop, so everything behind stays clickable and scrollable.
  useEffect(() => {
    if (!commentOpen && !replyOpen) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest("[data-composer-box]") || t.closest("[data-composer-toggle]")) return;
      setCommentOpen(false);
      setReplyOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setCommentOpen(false); setReplyOpen(false); }
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [commentOpen, replyOpen]);

  // Reset the draft whenever a different lead is selected.
  useEffect(() => {
    setCampaignId(lead.campaignId);
    setValues(initialValues());
    setEditedName(lead.name || "");
    setEditedPhone(lead.phone || "");
    setEditedEmail(lead.email || "");
    setAddingStatus(false); setNewStatus(""); setStatusError("");
    setAssignOpen(false);
    setTab("activity");
    setComposerText(""); setComposerError("");
    setNoteText(""); setNoteTitle(""); setNoteError(""); setShowNoteComposer(false);
    setShowEmailComposer(false);
    setReplyOpen(false);
    setCommentOpen(false);
    setOpenNoteMenu(null); setEditingNote(null); setDeletingNote(null);
    setNewMenuOpen(false);
    setFollowUpOpen(false); setFollowUpDue(""); setFollowUpNote(""); setFollowUpError("");
    setBookOpen(false); setBookDue(""); setBookNote(""); setBookError("");
  }, [lead.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty =
    campaignId !== lead.campaignId ||
    editedName.trim() !== (lead.name || "") ||
    editedPhone.trim() !== (lead.phone || "") ||
    editedEmail.trim() !== (lead.email || "") ||
    JSON.stringify(values) !== JSON.stringify(initialValues());

  const save = () => {
    // Keep a campaign's own Email question in sync when it is unanswered —
    // same mapping the New customer modal applies on create.
    const nextValues = { ...values };
    if (emailFieldId && !(nextValues[emailFieldId] || "").trim() && editedEmail.trim()) {
      nextValues[emailFieldId] = editedEmail.trim();
    }
    onSave(lead.id, {
      name: editedName.trim() || undefined,
      phone: editedPhone.trim() || undefined,
      email: editedEmail.trim() || undefined,
      campaignId,
      values: nextValues,
    });
  };

  const cancel = () => {
    setCampaignId(lead.campaignId);
    setValues(initialValues());
    setEditedName(lead.name || "");
    setEditedPhone(lead.phone || "");
    setEditedEmail(lead.email || "");
  };

  // Falls back to lead.phone when the campaign has no dedicated whatsapp
  // column — but the campaign can now remove the Phone question entirely, so
  // this can legitimately be empty, in which case there's nothing to chat to.
  const whatsappNumber = values[(campaign?.fields || []).find(f => f.kind === "whatsapp")?.id || ""] || values.whatsapp_number || lead.phone || "";
  const emailFieldId = (campaign?.fields || []).find(f => f.kind === "email")?.id || "";
  const email = values[emailFieldId] || values.email_address || lead.email || "";
  const company = lead.company || values.company || "";
  const members = team.data?.members ?? [];
  const agentName = (id?: string | null) => (id ? members.find(m => m.id === id)?.name ?? "Unknown" : "Unassigned");
  const agentOf = (id?: string | null) => (id ? members.find(m => m.id === id) : undefined);
  const assignee = agentOf(lead.assignedAgentId);
  const messageHref = whatsappNumber
    ? `https://wa.me/${whatsappNumber.replace(/\D/g, "")}`
    : email
      ? `mailto:${email}`
      : "";

  const rows = useRef<any[]>([]);
  rows.current = history.data ?? [];
  const byKind = (kinds: string[]) => rows.current.filter((r: any) => kinds.includes(r.kind));
  // Reference shows oldest first (created at the top, newest at the bottom).
  const chrono = (list: any[]) => [...list].sort((a, b) =>
    new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime());
  const timelineRows = chrono(rows.current);
  const callRows = chrono(byKind(["call"]));
  const commentRows = chrono(byKind(["comment"]));
  const noteRows = chrono(byKind(["note"]));
  const emailRows = chrono(byKind(["email"]));

  const lastContactAt = rows.current
    .map(r => r.createdAt)
    .filter((t): t is string => !!t)
    .sort()
    .pop();
  const nextFollowUp = (followUps.data ?? [])
    .filter(f => f.leadId === lead.id && !f.done)
    .sort((a, b) => a.dueAt.localeCompare(b.dueAt))[0];
  const leadFollowUps = (followUps.data ?? [])
    .filter(f => f.leadId === lead.id)
    .sort((a, b) => Number(a.done) - Number(b.done) || a.dueAt.localeCompare(b.dueAt));
  const leadAppointments = (appointmentsQuery.data ?? [])
    .filter((a: CrmAppointment) => a.leadId === lead.id)
    .sort((a: CrmAppointment, b: CrmAppointment) => a.dueAt.localeCompare(b.dueAt));

  const refreshHistory = async () => {
    await cache.invalidateQueries({ queryKey: ["crm-history", lead.id] });
  };

  const leadEmailDefault = email ? [email] : [];
  const emailSubjectDefault = `${lead.name || "Lead"} (#${contactIdOf(lead)})`;
  const emailSent = async () => {
    setReplyOpen(false);
    setShowEmailComposer(false);
    await refreshHistory();
    onSave(lead.id, { fresh: false });
  };

  const postComment = async () => {
    const text = composerText.trim();
    if (!text || composerBusy) return;
    setComposerBusy(true); setComposerError("");
    try {
      await crmApi.log(lead.id, { id: crypto.randomUUID(), kind: "comment", notes: text });
      setComposerText(""); setCommentOpen(false);
      await refreshHistory();
      onSave(lead.id, { fresh: false });
    } catch (e) {
      setComposerError(e instanceof Error ? e.message : "Unable to post comment.");
    } finally {
      setComposerBusy(false);
    }
  };

  const postNote = async () => {
    const title = noteTitle.trim();
    const body = noteText.trim();
    if ((!title && !body) || noteBusy) return;
    setNoteBusy(true); setNoteError("");
    try {
      const notes = title ? (body ? `${title}\n\n${body}` : title) : body;
      await crmApi.log(lead.id, { id: crypto.randomUUID(), kind: "note", notes });
      setNoteText(""); setNoteTitle(""); setShowNoteComposer(false);
      await refreshHistory();
      onSave(lead.id, { fresh: false });
    } catch (e) {
      setNoteError(e instanceof Error ? e.message : "Unable to save note.");
    } finally {
      setNoteBusy(false);
    }
  };

  const confirmDeleteNote = async () => {
    if (!deletingNote) return;
    try {
      await crmApi.deleteInteraction(lead.id, deletingNote.id);
      await refreshHistory();
    } catch (e) {
      setNoteError(e instanceof Error ? e.message : "Unable to delete note.");
    } finally {
      setDeletingNote(null);
    }
  };

  // Per-attempt playback, lead-scoped server-side: the URL is minted only
  // when this call belongs to a lead the viewer can see.
  const playRecording = async (rowId: string, callId: string) => {
    if (recordingUrls[rowId] || loadingRecordings[rowId]) return;
    setLoadingRecordings(prev => ({ ...prev, [rowId]: true }));
    try {
      const result = await crmApi.callRecordingUrl(callId);
      setRecordingUrls(prev => ({ ...prev, [rowId]: result.url }));
    } catch {
      /* keep the button; the attempt simply has no playable recording */
    } finally {
      setLoadingRecordings(prev => ({ ...prev, [rowId]: false }));
    }
  };

  const copyId = async () => {
    try { await navigator.clipboard.writeText(contactIdOf(lead)); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { /* clipboard unavailable */ }
  };

  const toggleFollowUp = async (followUpId: string, done: boolean) => {
    setTaskBusy(true); setTaskError("");
    try {
      await crmApi.editFollowUp(lead.id, followUpId, { done });
      await Promise.all([
        cache.invalidateQueries({ queryKey: ["crm-follow-ups"] }),
        cache.invalidateQueries({ queryKey: ["crm-history", lead.id] }),
      ]);
    } catch (e) {
      setTaskError(e instanceof Error ? e.message : "Unable to update follow-up.");
    } finally {
      setTaskBusy(false);
    }
  };

  const saveFollowUp = async () => {
    if (followUpBusy) return;
    if (!followUpDue || !followUpNote.trim()) { setFollowUpError("Pick a time and say what to discuss."); return; }
    if (new Date(followUpDue).getTime() <= Date.now()) { setFollowUpError("Choose a future time."); return; }
    setFollowUpBusy(true); setFollowUpError("");
    try {
      await crmApi.log(lead.id, { id: crypto.randomUUID(), kind: "follow-up", followUp: { dueAt: new Date(followUpDue).toISOString(), note: followUpNote.trim() } });
      await Promise.all([
        cache.invalidateQueries({ queryKey: ["crm-follow-ups"] }),
        cache.invalidateQueries({ queryKey: ["crm-agents-followups"] }),
        cache.invalidateQueries({ queryKey: ["crm-history", lead.id] }),
      ]);
      setFollowUpOpen(false); setFollowUpDue(""); setFollowUpNote("");
    } catch (e) {
      setFollowUpError(e instanceof Error ? e.message : "Unable to save follow-up.");
    } finally {
      setFollowUpBusy(false);
    }
  };

  const saveBooking = async () => {
    if (bookBusy) return;
    if (!bookDue) { setBookError("Pick a date and time."); return; }
    if (new Date(bookDue).getTime() <= Date.now()) { setBookError("Choose a future time."); return; }
    setBookBusy(true); setBookError("");
    try {
      await crmApi.createAppointment({ leadId: lead.id, dueAt: new Date(bookDue).toISOString(), note: bookNote.trim() || undefined });
      await cache.invalidateQueries({ queryKey: ["crm-appointments"] });
      setBookOpen(false); setBookDue(""); setBookNote("");
    } catch (e) {
      setBookError(e instanceof Error ? e.message : "Unable to book appointment.");
    } finally {
      setBookBusy(false);
    }
  };

  const contactRows: [string, string][] = [
    ["Phone", lead.phone || "—"],
    ["Email", email || "—"],
    ["Source", lead.source || "—"],
    ["Campaign", campaign?.name || "—"],
    ["Assigned agent", agentName(lead.assignedAgentId)],
    ["Last contact", lastContactAt ? formatDateTime(lastContactAt) : "Never contacted"],
    ["Next action", nextFollowUp ? formatDateTime(nextFollowUp.dueAt) : "—"],
  ];

  const railFieldRows: [string, string][] = (() => {
    if (!campaign) return [];
    const defs = (campaign.fields || []).filter(f => f.id !== "name" && f.id !== "phone");
    return defs
      .map((f): [string, string] => [f.label, values[f.id] ?? ""])
      .filter(([, v]) => v.trim() !== "");
  })();

  const actorOf = (row: any) => row.actorName || "Someone";
  const initialsFor = (name: string) => initialsOf(name === "Someone" ? "?" : name);

  const renderTimelineRow = (row: any) => {
    const when = <span className="ml-auto shrink-0 whitespace-nowrap text-xs text-ink-3">{ago(row.createdAt)}</span>;
    if (row.kind === "created") {
      return (
        <div className="grid grid-cols-[30px_minmax(0,1fr)] gap-2">
          <div className="relative flex justify-center before:absolute before:left-1/2 before:h-full before:border-l before:border-line">
            <Users className="relative z-10 h-6 w-6 bg-elevated p-1 text-ink-3" />
          </div>
          <div className="flex items-center gap-1.5 py-1 text-sm">
            <span className="font-semibold text-ink">Lead</span>
            <span className="text-ink-3">created this lead</span>
            {when}
          </div>
        </div>
      );
    }
    if (row.kind === "comment") {
      return (
        <div className="grid grid-cols-[30px_minmax(0,1fr)] gap-2">
          <div className="relative flex justify-center before:absolute before:left-1/2 before:h-full before:border-l before:border-line">
            <MessageCircle className="relative z-10 h-6 w-6 bg-elevated p-1 text-ink-3" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 py-1 text-sm">
              <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white", avatarColorOf(row.by || row.actorName || "?"))}>
                {initialsFor(actorOf(row))}
              </span>
              <p className="min-w-0 truncate">
                <span className="font-semibold text-ink">{actorOf(row)}</span>
                <span className="text-ink-3"> added a </span>
                <span className="font-semibold text-ink">comment</span>
              </p>
              {when}
            </div>
            <div className="rounded-lg bg-soft px-3 py-2 text-sm leading-6 text-ink-2">
              <RichText text={row.notes} />
            </div>
          </div>
        </div>
      );
    }
    if (row.kind === "call") {
      const dur = typeof row.duration === "number" && row.duration > 0
        ? `${Math.floor(row.duration / 60)}:${String(Math.floor(row.duration % 60)).padStart(2, "0")}` : "";
      const mins = typeof row.duration === "number" && row.duration > 0 ? `${Math.max(1, Math.round(row.duration / 60))}m` : "";
      // A batch/workflow call has no human actor — the AI agent that dialed is
      // the meaningful "who", and beats rendering it as "Someone".
      const caller = row.actorName || row.agentName || "Someone";
      return (
        <div className="grid grid-cols-[30px_minmax(0,1fr)] gap-2">
          <div className="relative flex justify-center before:absolute before:left-1/2 before:h-full before:border-l before:border-line">
            <PhoneOutgoing className="relative z-10 h-6 w-6 bg-elevated p-1 text-ink-3" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 py-1 text-sm">
              <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white", avatarColorOf(row.by || caller))}>
                {initialsFor(caller)}
              </span>
              <p className="min-w-0 truncate">
                <span className="font-semibold text-ink">{caller}</span>
                <span className="text-ink-3"> has made a call</span>
              </p>
              {when}
            </div>
            <div className="rounded-lg border border-line bg-canvas px-3 py-2.5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-ink">Outbound Call{row.outcome ? ` — ${row.outcome}` : ""}</p>
                <span className={cn("flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold text-white", avatarColorOf(row.by || caller))}>
                  {initialsFor(caller)}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                {row.createdAt && (
                  <span className="rounded-full bg-soft px-2.5 py-1 font-medium text-ink-2">
                    {format(new Date(row.createdAt), "MMM d, EEEE")}
                  </span>
                )}
                {mins && (
                  <span className="rounded-full bg-soft px-2.5 py-1 font-medium text-ink-2">{mins}</span>
                )}
                {row.outcome && (
                  <span className={cn("rounded-full px-2.5 py-1 font-semibold", OUTCOME_PILL[row.outcome] ?? "bg-soft text-ink-2")}>
                    {row.outcome}
                  </span>
                )}
                {row.classification && row.classification !== row.outcome && (
                  <span className="rounded-full bg-soft px-2.5 py-1 font-semibold text-ink-2">{row.classification}</span>
                )}
                {row.agentName && (
                  <span className="rounded-full bg-soft px-2.5 py-1 font-medium text-ink-2">
                    Agent: {row.agentName}{row.versionName ? ` · ${row.versionName}` : ""}
                  </span>
                )}
                {row.workflowName && (
                  <span className="rounded-full bg-soft px-2.5 py-1 font-medium text-ink-2">Workflow: {row.workflowName}</span>
                )}
                {dur && <span className="text-[11px] text-ink-3">Duration {dur}</span>}
                {row.recording?.available && (
                  recordingUrls[row.id] ? (
                    <audio controls src={recordingUrls[row.id]} className="h-8 w-full" />
                  ) : (
                    <button
                      disabled={loadingRecordings[row.id]}
                      onClick={() => void playRecording(row.id, row.callId || row.id)}
                      className="flex items-center gap-1.5 rounded-full bg-soft px-2.5 py-1 font-semibold text-ink-2 hover:bg-line disabled:opacity-50"
                    >
                      <Play className="h-3 w-3" />
                      {loadingRecordings[row.id] ? "Loading…" : "Listen"}
                    </button>
                  )
                )}
              </div>
              {row.summary && <p className="mt-2 text-xs text-ink-2">{row.summary}</p>}
              {Array.isArray(row.outfields) && row.outfields.length > 0 && (
                <div className="mt-2">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Outcome</p>
                  <div className="mt-1 grid grid-cols-2 gap-2">
                    {row.outfields.map((o: { field: string; value: string }) => (
                      <div key={o.field} className="rounded-lg bg-soft p-2">
                        <p className="font-mono text-[9px] uppercase tracking-wider text-ink-3">{o.field}</p>
                        <p className="mt-0.5 text-xs font-bold text-ink">{o.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {row.transcriptExcerpt && (
                <div className="mt-1">
                  <button
                    onClick={() => setOpenTranscripts(prev => ({ ...prev, [row.id]: !prev[row.id] }))}
                    className="text-[11px] font-semibold text-ink-3 underline underline-offset-2"
                  >
                    {openTranscripts[row.id] ? "Hide transcript" : "View transcript"}
                  </button>
                  {openTranscripts[row.id] && (
                    <p className="mt-1 whitespace-pre-wrap border-l-2 border-line pl-2 text-xs text-ink-3">{row.transcriptExcerpt}</p>
                  )}
                </div>
              )}
              {row.notes && <RichText text={row.notes} className="mt-2 text-xs text-ink-2" />}
              {row.nextAction && <p className="mt-1 text-xs text-ink-3">Next: {row.nextAction}</p>}
            </div>
          </div>
        </div>
      );
    }
    const KIND_META: Record<string, { icon: any; verb: string }> = {
      note: { icon: StickyNote, verb: "added a note" },
      followUp: { icon: Bell, verb: "scheduled a follow-up" },
      "follow-up": { icon: Bell, verb: "scheduled a follow-up" },
      assignment: { icon: UserPlus, verb: "changed assignment" },
      whatsapp: { icon: MessageCircle, verb: "sent a WhatsApp message" },
      sms: { icon: MessageSquare, verb: "sent an SMS" },
      email: { icon: Mail, verb: "logged an email" },
      compliance: { icon: ShieldAlert, verb: "blocked a call" },
      appointment: { icon: Calendar, verb: "has an appointment" },
    };
    const meta = KIND_META[row.kind] ?? KIND_META.note;
    const Icon = meta.icon;
    const body = row.kind === "assignment" && row.notes?.startsWith("assignedAgentId:")
      ? (() => {
          const arrow = row.notes.indexOf("→");
          const from = row.notes.slice("assignedAgentId:".length, arrow);
          const to = row.notes.slice(arrow + 1);
          const clean = (t: string) => { const s = t.trim(); return agentName(s.includes(" ") || s === "Unassigned" ? undefined : s) === "Unassigned" && s.includes(" ") ? s : agentName(s); };
          return arrow < 0 ? row.notes : `${clean(from)} → ${clean(to)}`;
        })()
      : row.notes;
    return (
      <div className="grid grid-cols-[30px_minmax(0,1fr)] gap-2">
        <div className="relative flex justify-center before:absolute before:left-1/2 before:h-full before:border-l before:border-line">
          <Icon className="relative z-10 h-6 w-6 bg-elevated p-1 text-ink-3" />
        </div>
        <div className="min-w-0 py-1">
          <div className="flex items-center gap-1.5 text-sm">
            <p className="min-w-0 truncate">
              <span className="font-semibold text-ink">{actorOf(row)}</span>
              <span className="text-ink-3"> {meta.verb}</span>
            </p>
            {when}
          </div>
          {body && <RichText text={body} className="mt-0.5 text-xs text-ink-2" />}
          {row.kind === "follow-up" && row.followUp && (
            <p className="mt-1 text-xs text-brand">
              Due {formatDateTime(row.followUp.dueAt)}{row.followUp.note ? ` · ${row.followUp.note}` : ""}
            </p>
          )}
          {row.kind === "appointment" && row.dueAt && (
            <p className={cn("mt-1 text-xs", row.status === "No-show" ? "text-red-600" : row.status === "Completed" ? "text-ink-3" : "text-brand")}>
              {row.status || "Scheduled"} · {formatDateTime(row.dueAt)}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-elevated">
      {/* Breadcrumb bar */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-2 sm:px-5">
        <div className="flex min-w-0 items-center gap-2 text-xs text-ink-3">
          {onBack && (
            <button onClick={onBack} className="flex shrink-0 items-center gap-1 font-medium text-ink-2 hover:text-brand">
              <ChevronLeft className="h-3.5 w-3.5" />
              <span>{listLabel}</span>
            </button>
          )}
          <span className="shrink-0 text-ink-4">/</span>
          <span className="truncate font-semibold text-ink text-sm">{lead.name || contactIdOf(lead) || "Unnamed lead"}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAssignOpen(true)}
            title={lead.assignedAgentId ? `Assigned to ${agentName(lead.assignedAgentId)}` : "Unassigned — click to assign"}
            className="flex items-center gap-1.5 rounded-lg bg-soft px-2 py-1 text-xs font-medium text-ink-2 hover:bg-line"
          >
            <span className={cn("flex h-4 w-4 items-center justify-center rounded-full text-[8px] font-bold text-white", avatarColorOf(lead.assignedAgentId || "?"))}>
              {initialsOf(assignee?.name || "?")}
            </span>
            <span className="max-w-[120px] truncate">{agentName(lead.assignedAgentId)}</span>
          </button>
          <Select value={leadStatus(lead)} onValueChange={(v: string) => { if (v === "__add__") setAddingStatus(true); else changeStatus(v); }}>
            <SelectTrigger aria-label="Lead status" className="w-auto gap-1.5 rounded-lg border border-line bg-transparent px-2 py-1 text-xs font-medium text-ink outline-none focus:border-brand">
              <span className="h-1.5 w-1.5 rounded-full bg-brand" />
              <SelectValue />
              <ChevronDown className="h-3 w-3 text-ink-3" />
            </SelectTrigger>
            <SelectContent>
              {statuses.map(status => <SelectItem key={status} value={status} className="focus:bg-soft focus:text-ink text-xs">{status}</SelectItem>)}
              <SelectItem value="__add__" className="focus:bg-soft text-xs"><span className="font-semibold text-brand">+ Add status</span></SelectItem>
            </SelectContent>
          </Select>
          <LeadCallButton
            lead={lead}
            onLogged={() => onSave(lead.id, {})}
            label="Call"
            triggerClassName={btnPrimaryCls}
          />
        </div>
      </div>
      {addingStatus && (
        <div className="mx-4 mt-2 flex max-w-sm flex-col gap-2 sm:mx-5">
          <input aria-label="New status name" autoFocus maxLength={40} value={newStatus} onChange={e => { setNewStatus(e.target.value); setStatusError(""); }} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addStatus(); } }} placeholder="e.g. Demo scheduled" className={fieldCls} />
          {statusError && <p role="alert" className="text-xs text-red-600">{statusError}</p>}
          <div className="flex gap-2"><button onClick={addStatus} className="rounded-lg bg-brand px-2.5 py-1 text-xs font-semibold text-white">Add</button><button onClick={() => { setAddingStatus(false); setNewStatus(""); setStatusError(""); }} className="text-xs text-ink-2">Cancel</button></div>
        </div>
      )}
      {overdueReasons && overdueReasons.length > 0 && (
        <p role="alert" className="mx-4 mt-2 rounded-lg bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-700 sm:mx-5">
          SLA breached: {overdueReasons.map(r => SLA_REASON_LABELS[r] ?? r).join(" · ")}
        </p>
      )}

      {/* Body — tabs + activity left, details rail right */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 overflow-hidden lg:grid-cols-[minmax(0,1fr)_310px]">
        <div className="flex h-full min-w-0 flex-col overflow-hidden border-line lg:border-r">
          {/* Icon tabs */}
          <div className="hide-scrollbar flex h-10 shrink-0 items-center gap-4 sm:gap-5 overflow-x-auto border-b border-line px-4 sm:px-5" role="tablist" aria-label="Record sections">
            {TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => { setTab(t.id); setChatOpen(false); }}
                className={cn(
                  "-mb-px flex shrink-0 items-center gap-1.5 border-b-2 py-2 text-xs transition-colors",
                  tab === t.id ? "border-brand font-semibold text-brand" : "border-transparent text-ink-3 hover:text-ink-2"
                )}
              >
                <t.icon className="h-3.5 w-3.5" />
                {t.label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3.5 sm:px-5">
            {tab === "activity" && (
              <>
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-xs font-bold uppercase tracking-wider text-ink-3">Activity</h2>
                  <div className="relative">
                    <button
                      onClick={() => setNewMenuOpen(o => !o)}
                      className={btnPrimaryCls}
                    >
                      <Plus className="h-3.5 w-3.5" /> New <ChevronDown className="h-3 w-3 opacity-70" />
                    </button>
                    {newMenuOpen && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setNewMenuOpen(false)} />
                        <div className="absolute right-0 z-20 mt-1.5 w-48 rounded-xl border border-line bg-elevated py-1 shadow-lg">
                          {[
                            { id: "comment", label: "Comment", icon: MessageCircle, run: () => setTab("comments") },
                            { id: "note", label: "Note", icon: StickyNote, run: () => setTab("notes") },
                            { id: "followup", label: "Follow-up", icon: Bell, run: () => setFollowUpOpen(true) },
                            { id: "event", label: "Appointment", icon: CalendarPlus, run: () => { setBookError(""); setBookOpen(true); } },
                          ].map(item => (
                            <button
                              key={item.id}
                              onClick={() => { setNewMenuOpen(false); item.run(); }}
                              className="flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm text-ink-2 hover:bg-soft hover:text-ink"
                            >
                              <item.icon className="h-4 w-4" /> {item.label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {history.isLoading ? (
                  <p className="py-8 text-center text-sm text-ink-3">Loading…</p>
                ) : timelineRows.length === 0 ? (
                  <p className="py-8 text-center text-sm text-ink-3">No activity yet.</p>
                ) : (
                  <div className="flex flex-col gap-5">
                    {timelineRows.map(row => <div key={row.id}>{renderTimelineRow(row)}</div>)}
                  </div>
                )}
              </>
            )}

            {tab === "emails" && (
              <>
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-xs font-bold uppercase tracking-wider text-ink-3">Emails</h2>
                  <button onClick={() => setShowEmailComposer(o => !o)} className={btnPrimaryCls}>
                    <Plus className="h-3.5 w-3.5" /> New Email
                  </button>
                </div>
                {showEmailComposer && (
                  <div className="mb-4">
                    <EmailComposer
                      key={`tab-${lead.id}`}
                      leadId={lead.id}
                      defaultTo={leadEmailDefault}
                      defaultSubject={emailSubjectDefault}
                      onSent={() => void emailSent()}
                      onDiscard={() => setShowEmailComposer(false)}
                    />
                  </div>
                )}
                {emailRows.length === 0 && !showEmailComposer ? (
                  <div className="flex flex-col items-center px-6 py-16 text-center">
                    <Mail className="h-8 w-8 text-ink-3" strokeWidth={1.5} />
                    <p className="mt-4 text-base font-semibold text-ink">No Emails Found</p>
                    <p className="mt-1 max-w-xs text-xs text-ink-3">No emails found in your inbox. New messages will appear here soon.</p>
                  </div>
                ) : emailRows.length > 0 && (
                  <div className="flex flex-col gap-2.5">
                    {emailRows.map(row => (
                      <div key={row.id} className="rounded-lg border border-line bg-canvas px-3.5 py-2.5">
                        <div className="flex items-center justify-between gap-2 text-xs">
                          <p className="truncate font-semibold text-ink">{actorOf(row)} <span className="font-normal text-ink-3">sent an email</span></p>
                          <span className="shrink-0 text-ink-3">{ago(row.createdAt)}</span>
                        </div>
                        {row.notes && <RichText text={row.notes} className="mt-1 text-xs leading-5 text-ink-2" />}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {tab === "comments" && (
              <>
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-xs font-bold uppercase tracking-wider text-ink-3">Comments</h2>
                  <button
                    onClick={() => {
                      setComposerError("");
                      setCommentOpen(true);
                      setReplyOpen(false);
                      setChatOpen(false);
                      requestAnimationFrame(() => composerRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }));
                    }}
                    className={btnPrimaryCls}
                  >
                    <Plus className="h-3.5 w-3.5" /> New Comment
                  </button>
                </div>
                {commentRows.length === 0 ? (
                  <p className="py-8 text-center text-xs text-ink-3">No comments yet. Use the box below to start the discussion.</p>
                ) : (
                  <div className="flex flex-col gap-4">
                    {commentRows.map(row => <div key={row.id}>{renderTimelineRow(row)}</div>)}
                  </div>
                )}
              </>
            )}

            {tab === "data" && (
              <>
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-xs font-bold uppercase tracking-wider text-ink-3">Data</h2>
                  <button
                    disabled={!dirty}
                    onClick={save}
                    className={btnPrimaryCls}
                  >
                    <Save className="h-3.5 w-3.5" /> Save
                  </button>
                </div>
                <div ref={formRef} className="scroll-mt-4 pb-8">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Full name</label>
                      <input value={editedName} onChange={(e) => setEditedName(e.target.value)} placeholder="e.g. Sunita Mehta" className={fieldCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Phone number</label>
                      <input value={editedPhone} onChange={(e) => setEditedPhone(e.target.value)} placeholder="10-digit mobile" className={fieldCls} />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className={labelCls}>Email</label>
                    <input value={editedEmail} onChange={(e) => setEditedEmail(e.target.value)} placeholder="name@company.in" className={fieldCls} />
                  </div>
                  <div className="mt-4">
                    <label className={labelCls}>Campaign</label>
                    <Select value={campaignId} onValueChange={(v: string) => { setCampaignId(v); setValues({}); }}>
                      <SelectTrigger className={fieldCls}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {campaigns.map((c) => (
                          <SelectItem key={c.id} value={c.id} className="focus:bg-soft focus:text-ink text-xs">{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {campaign && (campaign.fields || []).filter((f) => f.id !== "name" && f.id !== "phone").length > 0 && (
                    <div className="mt-4 space-y-4">
                      {(campaign.fields || [])
                        .filter((f) => f.id !== "name" && f.id !== "phone")
                        // Demo Request forms send varying shapes (pilot: 5 fields,
                        // homepage demo: 4 fields) — show only filled ones so an
                        // empty Expected Volume etc. never renders. Other
                        // campaigns keep every field editable.
                        .filter((f) => (campaign?.name !== "Demo Request" && campaign?.name !== "Website Demo") || (values[f.id] ?? "").trim() !== "")
                        .map((f) => (
                        <div key={f.id}>
                          <label className={labelCls}>{f.label}</label>
                          <CustomFieldInput
                            field={f}
                            value={values[f.id] ?? ""}
                            onChange={(v) => setValues((prev) => ({ ...prev, [f.id]: v }))}
                            inputClassName={fieldCls}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-3">
                    <label className={labelCls}>Created</label>
                    <p className="text-xs text-ink-2">{timeAgo(lead.createdAt)}</p>
                  </div>
                  {dirty && (
                    <div className="mt-3 flex gap-2">
                      <button onClick={cancel} className="rounded-lg px-2.5 py-1 text-xs font-semibold text-ink-2 hover:bg-soft">Cancel</button>
                      <button onClick={save} className={btnPrimaryCls}>
                        <Save className="h-3.5 w-3.5" /> Save changes
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}

            {tab === "events" && (
              <>
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-xs font-bold uppercase tracking-wider text-ink-3">Events</h2>
                  <button onClick={() => { setBookError(""); setBookOpen(true); }} className={btnPrimaryCls}>
                    <Plus className="h-3.5 w-3.5" /> Schedule
                  </button>
                </div>
                {appointmentsQuery.isLoading ? (
                  <p className="py-8 text-center text-xs text-ink-3">Loading…</p>
                ) : leadAppointments.length === 0 ? (
                  <p className="py-8 text-center text-xs text-ink-3">No events scheduled.</p>
                ) : (
                  <div className="flex flex-col gap-2.5">
                    {leadAppointments.map((a: CrmAppointment) => (
                      <div key={a.id} className="flex gap-2.5 rounded-lg border border-line bg-elevated px-3 py-2">
                        <span className="w-[3px] shrink-0 rounded-full bg-green-500" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs font-semibold text-ink">{a.note || "Appointment"}</p>
                          <p className="mt-0.5 text-[11px] text-ink-3">{a.dueAt ? format(new Date(a.dueAt), "MMM d, EEEE h:mm a") : "—"}</p>
                        </div>
                        <span className={cn("h-fit shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold", appointmentPillCls(a.status))}>{a.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {tab === "calls" && (
              <>
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-xs font-bold uppercase tracking-wider text-ink-3">Calls</h2>
                  <LeadCallButton
                    lead={lead}
                    onLogged={() => onSave(lead.id, {})}
                    label="Call"
                    triggerClassName={btnPrimaryCls}
                  />
                </div>
                {callRows.length === 0 ? (
                  <p className="py-8 text-center text-xs text-ink-3">No calls logged yet.</p>
                ) : (
                  <div className="flex flex-col gap-4">
                    {callRows.map(row => <div key={row.id}>{renderTimelineRow(row)}</div>)}
                  </div>
                )}
                <div className="mt-4 border-t border-line pt-3">
                  <ContactCallHistory key={lead.id} contactId={contactIdOf(lead)} />
                </div>
              </>
            )}

            {tab === "tasks" && (
              <>
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-xs font-bold uppercase tracking-wider text-ink-3">Tasks</h2>
                  <button onClick={() => setFollowUpOpen(true)} className={btnPrimaryCls}>
                    <Plus className="h-3.5 w-3.5" /> New
                  </button>
                </div>
                {taskError && <p role="alert" className="mb-2 text-xs font-semibold text-red-500">{taskError}</p>}
                {leadFollowUps.length === 0 ? (
                  <p className="py-8 text-center text-xs text-ink-3">No tasks on this record.</p>
                ) : (
                  <div className="flex flex-col divide-y divide-line">
                    {leadFollowUps.map((f) => (
                      <div key={f.id} className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 hover:bg-soft">
                        <button
                          disabled={taskBusy}
                          onClick={() => void toggleFollowUp(f.id, !f.done)}
                          aria-label={f.done ? "Reopen task" : "Complete task"}
                          className={cn(
                            "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors disabled:opacity-50",
                            f.done ? "border-green-500 bg-green-500 text-white" : "border-line text-transparent hover:border-green-500"
                          )}
                        >
                          <Check className="h-2.5 w-2.5" />
                        </button>
                        <div className="min-w-0 flex-1">
                          <p className={cn("truncate text-xs font-medium text-ink", f.done && "text-ink-3 line-through")}>{f.note || "Follow-up"}</p>
                          <p className="mt-0.5 text-[11px] text-ink-3">
                            {f.dueAt ? format(new Date(f.dueAt), "MMM d, h:mm a") : "No due date"}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {tab === "notes" && (
              <>
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-xs font-bold uppercase tracking-wider text-ink-3">Notes</h2>
                  <button onClick={() => { setNoteError(""); setShowNoteComposer(o => !o); }} className={btnPrimaryCls}>
                    <Plus className="h-3.5 w-3.5" /> New Note
                  </button>
                </div>
                {showNoteComposer && (
                  <div className="mb-4 rounded-lg border border-line bg-soft/50 p-2.5">
                    <input
                      value={noteTitle}
                      onChange={e => setNoteTitle(e.target.value)}
                      placeholder="Title"
                      maxLength={120}
                      aria-label="Note title"
                      className="w-full rounded-lg border border-line bg-elevated px-3 py-1.5 text-xs font-semibold text-ink outline-none focus:border-brand"
                    />
                    <textarea
                      value={noteText}
                      onChange={e => { setNoteText(e.target.value); setNoteError(""); }}
                      placeholder="Write a note…"
                      rows={3}
                      maxLength={10000}
                      className="mt-2 w-full resize-none rounded-lg border border-line bg-elevated px-3 py-1.5 text-xs text-ink outline-none focus:border-brand"
                    />
                    {noteError && <p role="alert" className="mt-1 text-xs text-red-600">{noteError}</p>}
                    <div className="mt-2 flex justify-end gap-2">
                      <button onClick={() => { setShowNoteComposer(false); setNoteTitle(""); setNoteText(""); setNoteError(""); }} className="rounded-lg px-2.5 py-1 text-xs font-semibold text-ink-2 hover:bg-soft">Cancel</button>
                      <button disabled={noteBusy || (!noteTitle.trim() && !noteText.trim())} onClick={() => void postNote()} className={cn(btnPrimaryCls, "px-3 py-1")}>
                        {noteBusy ? "Saving…" : "Save"}
                      </button>
                    </div>
                  </div>
                )}
                {noteRows.length === 0 && !showNoteComposer ? (
                  <p className="py-8 text-center text-sm text-ink-3">No notes yet.</p>
                ) : (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {noteRows.map(row => {
                      const [title, body] = splitNoteTitle(row.notes);
                      return (
                        <div
                          key={row.id}
                          onClick={() => { setEditingNote({ id: row.id, leadId: lead.id, notes: row.notes || "" }); setOpenNoteMenu(null); }}
                          className="flex h-48 cursor-pointer flex-col justify-between gap-2 rounded-xl bg-soft px-4 py-3 hover:bg-line/60"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="truncate text-sm font-semibold text-ink">{title}</p>
                            <div className="relative shrink-0" onClick={e => e.stopPropagation()}>
                              <button
                                aria-label="Note options"
                                onClick={() => setOpenNoteMenu(openNoteMenu === row.id ? null : row.id)}
                                className="rounded-md p-1 text-ink-2 hover:bg-line hover:text-ink"
                              >
                                <span className="text-base font-bold leading-none tracking-widest">···</span>
                              </button>
                              {openNoteMenu === row.id && (
                                <>
                                  <div className="fixed inset-0 z-10" onClick={() => setOpenNoteMenu(null)} />
                                  <div className="absolute right-0 z-20 mt-1 w-36 rounded-xl border border-line bg-elevated py-1 shadow-lg">
                                    <button
                                      onClick={() => { setEditingNote({ id: row.id, leadId: lead.id, notes: row.notes || "" }); setOpenNoteMenu(null); }}
                                      className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-xs text-ink-2 hover:bg-soft hover:text-ink"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => { setDeletingNote({ id: row.id }); setOpenNoteMenu(null); }}
                                      className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-xs text-red-600 hover:bg-red-500/10"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                          {body && (
                            <div className="flex-1 overflow-hidden text-[13px] leading-5 text-ink-3">
                              <RichText text={body} />
                            </div>
                          )}
                          <div className="mt-1 flex items-center justify-between gap-2 text-xs">
                            <span className="flex min-w-0 items-center gap-1.5">
                              <span className={cn("flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white", avatarColorOf(row.by || row.actorName || "?"))}>
                                {initialsFor(actorOf(row))}
                              </span>
                              <span className="truncate font-medium text-ink">{actorOf(row)}</span>
                            </span>
                            <span className="shrink-0 text-ink-3">{ago(row.createdAt)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {tab === "attachments" && (
              <>
                <div className="mb-5 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-ink">Attachments</h2>
                </div>
                <p className="py-8 text-center text-sm text-ink-3">No attachments on this record.</p>
              </>
            )}
          </div>

          {/* Bottom composer — comment box pinned under the timeline */}
          {(tab === "activity" || tab === "comments") && (
            <div ref={composerRef} className="sticky bottom-0 border-t border-line bg-elevated px-4 py-2.5 sm:px-6 scroll-mt-4">
              <div className="flex items-center gap-5">
                <button
                  onClick={() => { setReplyOpen(o => !o); setCommentOpen(false); setChatOpen(false); }}
                  data-composer-toggle
                  className="flex items-center gap-1.5 text-sm font-medium text-ink-2 hover:text-ink"
                >
                  <Mail className="h-4 w-4" /> Reply
                </button>
                <button onClick={() => { setComposerError(""); setCommentOpen(o => !o); setReplyOpen(false); setChatOpen(false); }} data-composer-toggle className="flex items-center gap-1.5 text-sm font-medium text-ink-2 hover:text-ink">
                  <MessageCircle className="h-4 w-4" /> Comment
                </button>
              </div>
              {replyOpen && (
                <div data-composer-box className="mt-2.5">
                  <EmailComposer
                    key={`reply-${lead.id}`}
                    leadId={lead.id}
                    defaultTo={leadEmailDefault}
                    defaultSubject={emailSubjectDefault}
                    onSent={() => void emailSent()}
                    onDiscard={() => setReplyOpen(false)}
                  />
                </div>
              )}
              {commentOpen && (
                <div data-composer-box className="mt-2.5 rounded-xl border border-line bg-elevated">
                  <textarea
                    ref={commentBoxRef}
                    autoFocus
                    value={composerText}
                    onChange={e => { setComposerText(e.target.value); setComposerError(""); }}
                    onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void postComment(); } }}
                    placeholder="Add a comment… (Ctrl+Enter to post)"
                    rows={3}
                    maxLength={10000}
                    aria-label="Add a comment"
                    className="w-full resize-y bg-transparent px-3 py-2 text-sm text-ink outline-none"
                  />
                  <div className="flex items-center justify-between gap-2 border-t border-line px-3 py-2">
                    <ComposerToolbar targetRef={commentBoxRef} value={composerText} onChange={v => { setComposerText(v); setComposerError(""); }} />
                    <div className="flex gap-2">
                      <button onClick={() => setCommentOpen(false)} className="rounded-lg px-2.5 py-1 text-xs font-semibold text-ink-2 hover:bg-soft">Discard</button>
                      <button disabled={composerBusy || !composerText.trim()} onClick={() => void postComment()} className={cn(btnPrimaryCls, "px-3 py-1")}>
                        {composerBusy ? "Posting…" : "Post"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {composerError && <p role="alert" className="mt-1.5 text-xs text-red-600">{composerError}</p>}
            </div>
          )}

          {chatOpen && (
            <div className="m-4 h-96 shrink-0 overflow-hidden rounded-xl border border-line sm:m-6 sm:mt-0">
              <WhatsAppChatPanel key={lead.id + "-whatsapp"} phone={whatsappNumber} name={lead.name} />
            </div>
          )}
        </div>

        {/* Right rail — id row fixed (same 40px as the tabs row, so both scrollbars start aligned); everything below scrolls */}
        <aside className="flex h-full min-w-0 flex-col overflow-hidden border-t border-line lg:border-l lg:border-t-0">
          <button onClick={copyId} title="Click to copy record id" className="flex h-10 w-full shrink-0 items-center bg-elevated px-4 text-left text-xs font-mono font-semibold text-ink shadow-[inset_0_-1px_0_var(--color-line)] hover:bg-soft">
            <span className="truncate">{contactIdOf(lead)}</span>
            {copied && <Check className="ml-2 h-3.5 w-3.5 shrink-0 text-green-600" />}
          </button>

          <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex items-center gap-3 border-b border-line px-4 py-3">
            <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white", avatarColorOf(lead.id))}>
              {initialsOf(lead.name)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-base font-bold text-ink">{lead.name || contactIdOf(lead) || "Unnamed lead"}</p>
              <div className="mt-1 flex gap-1">
                {whatsappNumber && (
                  <button title="Chat" onClick={() => { onSave(lead.id, { fresh: false }); setChatOpen(o => !o); setReplyOpen(false); setCommentOpen(false); setComposerError(""); }} className={cn("rounded-md p-1.5", chatOpen ? "bg-brand/10 text-brand" : "bg-soft text-ink-2 hover:bg-line hover:text-ink")}>
                    <MessageCircle className="h-3.5 w-3.5" />
                  </button>
                )}
                {messageHref && (
                  <a title="SMS" onClick={() => onSave(lead.id, { fresh: false })} href={messageHref} target={messageHref.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="rounded-md bg-soft p-1.5 text-ink-2 hover:bg-line hover:text-ink">
                    <MessageSquare className="h-3.5 w-3.5" />
                  </a>
                )}
                {team.data?.permissions?.assign_leads && (
                  <button title="Assign" onClick={() => setAssignOpen(true)} className="rounded-md bg-soft p-1.5 text-ink-2 hover:bg-line hover:text-ink">
                    <UserCog className="h-3.5 w-3.5" />
                  </button>
                )}
                <button title="Delete" onClick={() => onDelete(lead.id)} className="rounded-md bg-red-500/10 p-1.5 text-red-600 hover:bg-red-500/15">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>

          <div className="border-b border-line px-4 py-2.5">
            <button onClick={() => setDetailsOpen(o => !o)} className="flex w-full items-center gap-1.5 text-xs font-bold text-ink">
              <ChevronDown className={cn("h-3.5 w-3.5 text-ink-3 transition-transform", !detailsOpen && "-rotate-90")} />
              Details
            </button>
            {detailsOpen && (
              <div className="mt-2">
                {contactRows.map(([label, value]) => (
                  <div key={label} className="flex items-baseline justify-between gap-3 border-b border-line/70 py-1.5 text-xs last:border-0">
                    <span className="shrink-0 truncate text-ink-3">{label}</span>
                    <span className={cn("min-w-0 truncate text-right font-medium text-ink", label === "Phone" && "font-mono")}>{value}</span>
                  </div>
                ))}
                {lead.notes && (
                  <div className="mt-2 rounded-lg bg-soft p-2.5 text-xs leading-relaxed text-ink-2">
                    <span aria-hidden>📝 </span>{lead.notes}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="px-4 py-2.5">
            <button onClick={() => setCampaignOpen(o => !o)} className="flex w-full items-center gap-1.5 text-xs font-bold text-ink">
              <ChevronDown className={cn("h-3.5 w-3.5 text-ink-3 transition-transform", !campaignOpen && "-rotate-90")} />
              Campaign
            </button>
            {campaignOpen && (
              <div className="mt-2">
                <p className="text-xs font-medium text-ink">{campaign?.name || "—"}</p>
                {railFieldRows.length > 0 && (
                  <div className="mt-1.5">
                    {railFieldRows.map(([label, value]) => (
                      <div key={label} className="flex items-baseline justify-between gap-3 border-b border-line/70 py-1.5 text-xs last:border-0">
                        <span className="shrink-0 truncate text-ink-3">{label}</span>
                        <span className="min-w-0 truncate text-right font-medium text-ink">{value}</span>
                      </div>
                    ))}
                  </div>
                )}
                <button onClick={() => setTab("data")} className="mt-2 text-xs font-semibold text-brand hover:underline">Edit in Data tab →</button>
              </div>
            )}
          </div>
          </div>{/* end rail scroll body */}
        </aside>
      </div>

      {editingNote && (
        <NoteEditModal
          fixedLeadId={lead.id}
          leads={[]}
          note={editingNote}
          onClose={() => setEditingNote(null)}
          onSaved={() => { setEditingNote(null); void refreshHistory(); }}
        />
      )}

      {deletingNote && (
        <ConfirmModal
          title="Delete note"
          body="Remove this note? This can't be undone."
          confirmLabel="Delete"
          onClose={() => setDeletingNote(null)}
          onConfirm={() => void confirmDeleteNote()}
        />
      )}

      {assignOpen && (
        <AssignLeadModal
          lead={lead}
          agents={members}
          currentAgentName={agentName(lead.assignedAgentId)}
          onClose={() => setAssignOpen(false)}
          onSave={async (agentId) => { onSave(lead.id, { assignedAgentId: agentId }); }}
        />
      )}

      {followUpOpen && (
        <Modal
          title="Schedule follow-up"
          subtitle={`${lead.name || lead.phone || "This lead"}`}
          onClose={() => !followUpBusy && setFollowUpOpen(false)}
          width="max-w-md"
          footer={
            <>
              <button disabled={followUpBusy} onClick={() => setFollowUpOpen(false)} className="rounded-lg px-4 py-2 text-sm font-semibold text-ink-2 hover:bg-soft">Cancel</button>
              <button disabled={followUpBusy} onClick={() => void saveFollowUp()} className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-50">
                {followUpBusy ? "Saving…" : "Save follow-up"}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Due date &amp; time</label>
              <DateTimePicker
                value={followUpDue}
                onChange={val => { setFollowUpDue(val); setFollowUpError(""); }}
                placeholder="Pick date & time"
                className="w-full"
              />
            </div>
            <div>
              <label className={labelCls}>What to discuss</label>
              <textarea required maxLength={2000} value={followUpNote} onChange={e => { setFollowUpNote(e.target.value); setFollowUpError(""); }} placeholder="e.g. Send the quote" rows={3} className={fieldCls + " resize-none"} />
            </div>
            {followUpError && <p role="alert" className="text-xs font-semibold text-red-500">{followUpError}</p>}
          </div>
        </Modal>
      )}

      {bookOpen && (
        <Modal
          title="Book appointment"
          subtitle={`${lead.name || lead.phone || "This lead"}`}
          onClose={() => !bookBusy && setBookOpen(false)}
          width="max-w-md"
          footer={
            <>
              <button disabled={bookBusy} onClick={() => setBookOpen(false)} className="rounded-lg px-4 py-2 text-sm font-semibold text-ink-2 hover:bg-soft">Cancel</button>
              <button disabled={bookBusy} onClick={() => void saveBooking()} className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-50">
                {bookBusy ? "Booking…" : "Book appointment"}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Date &amp; time</label>
              <DateTimePicker
                value={bookDue}
                onChange={val => { setBookDue(val); setBookError(""); }}
                placeholder="Pick date & time"
                className="w-full"
              />
            </div>
            <div>
              <label className={labelCls}>Agenda (optional)</label>
              <textarea maxLength={2000} value={bookNote} onChange={e => { setBookNote(e.target.value); setBookError(""); }} placeholder="e.g. Product demo" rows={3} className={fieldCls + " resize-none"} />
            </div>
            {bookError && <p role="alert" className="text-xs font-semibold text-red-500">{bookError}</p>}
          </div>
        </Modal>
      )}
    </div>
  );
}
